"use client";

import { createClient as createSupabaseClient } from "@/utils/supabase/client";
import { IS_DEMO_BUILD } from "@/utils/supabase/config";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type DatabaseEnvironment = "local" | "production" | "demo";

interface DatabaseContextType {
  currentDatabase: DatabaseEnvironment;
  setCurrentDatabase: (env: DatabaseEnvironment) => void;
  isProductionMode: boolean;
  isDevelopmentMode: boolean;
  supabaseClient: ReturnType<typeof createSupabaseClient> | null;
}

const DatabaseContext = createContext<DatabaseContextType | undefined>(
  undefined,
);

const isDatabaseEnvironment = (
  value: string | null | undefined,
): value is DatabaseEnvironment => {
  return value === "local" || value === "production" || value === "demo";
};

const getPersistedDatabaseEnvironment = (): DatabaseEnvironment | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const match = document.cookie.match(/(?:^|; )db_env=([^;]*)/);
  const cookieEnv = match ? decodeURIComponent(match[1]) : null;
  const savedEnv = sessionStorage.getItem("database-environment");
  const candidate = cookieEnv || savedEnv;

  return isDatabaseEnvironment(candidate) ? candidate : null;
};

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (context === undefined) {
    throw new Error("useDatabase must be used within a DatabaseProvider");
  }
  return context;
};

export const useOptionalDatabase = () => {
  return useContext(DatabaseContext);
};

interface DatabaseProviderProps {
  children: React.ReactNode;
}

export const DatabaseProvider: React.FC<DatabaseProviderProps> = ({
  children,
}) => {
  const supabaseUrlLocal = process.env.NEXT_PUBLIC_SUPABASE_URL_LOCAL || "";
  const hasLocalSupabase =
    supabaseUrlLocal.includes("localhost") ||
    supabaseUrlLocal.includes("127.0.0.1");

  const isProductionMode = process.env.NODE_ENV === "production";
  const isDevelopmentMode =
    process.env.NODE_ENV === "development" && hasLocalSupabase;

  const [currentDatabase, setCurrentDatabaseState] =
    useState<DatabaseEnvironment>(() => {
      // The demo build is pinned to the demo database (see IS_DEMO_BUILD).
      if (IS_DEMO_BUILD) {
        return "demo";
      }

      if (isProductionMode) {
        return "production";
      }

      if (isDevelopmentMode) {
        const persistedEnv = getPersistedDatabaseEnvironment();

        if (persistedEnv) {
          return persistedEnv;
        }
      }

      return "local";
    });

  const [supabaseClient, setSupabaseClient] = useState<ReturnType<
    typeof createSupabaseClient
  > | null>(null);
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isDevelopmentMode) {
      setHasHydrated(true);
      return;
    }

    setHasHydrated(true);
  }, [isDevelopmentMode]);

  useEffect(() => {
    if (typeof window === "undefined" || !hasHydrated) return;
    if (!isDevelopmentMode) return;

    document.cookie = `db_env=${currentDatabase}; path=/`;
    sessionStorage.setItem("database-environment", currentDatabase);
  }, [currentDatabase, hasHydrated, isDevelopmentMode]);

  useEffect(() => {
    try {
      const newClient = createSupabaseClient();
      setSupabaseClient(newClient);
    } catch (error) {
      console.error("Error creating Supabase client:", error);
      setSupabaseClient(null);
    }
  }, [currentDatabase]);

  const setCurrentDatabase = useCallback(
    (env: DatabaseEnvironment) => {
      if (!isDevelopmentMode) {
        return;
      }

      if (!isDatabaseEnvironment(env)) {
        return;
      }

      setCurrentDatabaseState(env);
    },
    [isDevelopmentMode],
  );

  useEffect(() => {
    if (process.env.NODE_ENV === "development" && !hasLocalSupabase) {
      console.warn(
        "Database switching is disabled because local Supabase is not configured.",
      );
    }
  }, [hasLocalSupabase]);

  const value: DatabaseContextType = {
    currentDatabase,
    setCurrentDatabase,
    isProductionMode,
    isDevelopmentMode,
    supabaseClient,
  };

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};
